import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@artstorefronts/ui';
import { Shield, ExternalLinkIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArtGuard } from './ArtGuard';
import { useAnalytics } from '~/context/analytics';
import { useEffect } from 'react';

interface ArtGuardModalProps {
  imageId: string;
  imageTitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: 'chat_imagebox' | 'art_page';
}

function ArtGuardModal({ imageId, imageTitle, open, onOpenChange, source = 'art_page' }: ArtGuardModalProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { amplitude } = useAnalytics();

  useEffect(() => {
    if (open) {
      amplitude.track('artguard_modal_opened', { image_id: imageId, source });
    }
  }, [open, imageId, source, amplitude]);

  const handleViewFullPage = () => {
    amplitude.track('artguard_view_full_page_clicked', { image_id: imageId });
    onOpenChange(false);
    navigate(`/art/${imageId}?tab=artguard`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-full max-h-[80vh] sm:max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center text-sm sm:text-base">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mr-2" />
            <span className="truncate">
              {t('art-guard.scan-results')}
              {imageTitle && <span className="text-gray-500 hidden sm:inline">- {imageTitle}</span>}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-auto flex-1 min-h-0">
          <ArtGuard imageId={imageId} viewLocation="modal" />
        </div>

        <DialogFooter className="flex-shrink-0 flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            {tc('close')}
          </Button>
          <Button variant="default" onClick={handleViewFullPage} className="w-full sm:w-auto">
            <ExternalLinkIcon className="size-4" />
            <span className="hidden sm:inline">{t('art-guard.view-full-page')}</span>
            <span className="sm:hidden">{t('art-guard.full-page')}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ArtGuardModal;
